import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ShieldCheck, ChevronRight } from "lucide-react";
import {
  createWarrantyRazorpayOrder,
  verifyWarrantyPayment,
} from "../api/paymentApi";
import { getStoredUser } from "../api/authApi";
import { toast } from "../hooks/use-toast";

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Load Razorpay SDK (same as Checkout.jsx)
const loadRazorpay = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function WarrantyCheckout() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [processing, setProcessing] = useState(false);

  // All data passed via navigate() state from WarrantyRenewalPage
  const {
    orderId,
    productId,
    renewalDuration,
    renewalPrice,
    productName,
    newExpiry, // ISO string – new warrantyEndDate after renewal
    previousWarrantyEndDate,
    orderShippingAddress,
    orderContactPhone,
  } = state || {};

  // Guard: if someone navigates here directly without state, go back
  // NOTE: renewalPrice can be 0 (free renewal), so we use nullish check, not falsy check
  if (!orderId || !productId || renewalPrice == null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 transition-colors">
        <div className="text-center">
          <ShieldCheck className="w-12 h-12 text-gray-300 dark:text-slate-700 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400 font-medium">No renewal data found.</p>
          <button
            onClick={() => navigate("/profile")}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700"
          >
            Back to Profile
          </button>
        </div>
      </div>
    );
  }

  const handlePay = async () => {
    setProcessing(true);
    try {
      // 1 — Load Razorpay SDK
      const isLoaded = await loadRazorpay();
      if (!isLoaded) {
        toast({
          title: "Error",
          description: "Razorpay SDK failed to load. Are you online?",
          variant: "destructive",
        });
        setProcessing(false);
        return;
      }

      const user = getStoredUser();
      const userEmail = user?.email || "";

      // 2 — Create Razorpay order on backend
      // API: POST /api/laptops/payment/create-warranty-order
      // Body: { renewalPrice: Number }
      const orderResponse = await createWarrantyRazorpayOrder(renewalPrice);
      if (!orderResponse.success)
        throw new Error("Failed to create warranty payment order");

      const { data: razorpayOrder, key } = orderResponse;

      // 3 — Open Razorpay modal
      const options = {
        key,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        name: "Bright Laptop",
        description: `Warranty Renewal — ${renewalDuration} Month Extension`,
        image: "/logo.png",
        order_id: razorpayOrder.id,

        handler: async function (response) {
          try {
            // 4 — Verify payment on backend and update warrantyEndDate
            // API: POST /api/laptops/payment/verify-warranty-payment
            // Body:
            //   razorpay_order_id:   String  (from Razorpay)
            //   razorpay_payment_id: String  (from Razorpay)
            //   razorpay_signature:  String  (from Razorpay)
            //   orderId:             String  (original order _id)
            //   productId:           String  (product _id in that order)
            //   renewalPrice:        Number  (amount paid)
            //   newWarrantyEndDate:  String  (ISO date – new expiry)
            const verifyResponse = await verifyWarrantyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              orderId,
              productId,
              renewalPrice,
              renewalDuration,
              newWarrantyEndDate: newExpiry,
            });

            if (verifyResponse.success) {
              toast({
                title: "Warranty Renewed!",
                description: "Your warranty has been extended successfully.",
              });
              // 5 — Navigate to receipt page
              navigate("/warranty-receipt", {
                state: {
                  productName,
                  renewalDuration,
                  renewalPrice,
                  previousWarrantyEndDate,
                  newWarrantyEndDate:
                    verifyResponse.data.newWarrantyEndDate || newExpiry,
                  razorpayPaymentId: response.razorpay_payment_id,
                  orderId,
                  userName: user?.name || "",
                  userEmail: userEmail,
                  // Prefer the original order's shipping address/phone over user profile
                  userPhone:
                    orderContactPhone ||
                    user?.addresses?.find((a) => a.isDefault)?.phone ||
                    user?.addresses?.[0]?.phone ||
                    user?.phone ||
                    "",
                  userAddress:
                    orderShippingAddress ||
                    user?.addresses?.find((a) => a.isDefault) ||
                    user?.addresses?.[0] ||
                    null,
                },
              });
            } else {
              throw new Error("Verification failed");
            }
          } catch (err) {
            console.error("Warranty payment verification error:", err);
            toast({
              title: "Verification Failed",
              description:
                "Payment was received but verification failed. Please contact support.",
              variant: "destructive",
            });
          }
        },

        prefill: { name: user?.name || "", email: userEmail },
        theme: { color: "#004792" },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();

      paymentObject.on("payment.failed", function (resp) {
        toast({
          title: "Payment Failed",
          description:
            resp.error?.description || "Payment failed. Please try again.",
          variant: "destructive",
        });
      });
    } catch (error) {
      console.error("Warranty checkout error:", error);
      toast({
        title: "Checkout Failed",
        description:
          error.response?.data?.error ||
          error.message ||
          "Failed to initiate payment.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-950 transition-colors">
      {/* Header */}
      <div className="bg-[#004792] dark:bg-slate-900 border-b border-transparent dark:border-slate-800 transition-colors">
        <div className="max-w-2xl mx-auto px-4 py-4 sm:py-5 flex items-center justify-between gap-3">
          <h1 className="text-white font-bold text-base sm:text-xl uppercase tracking-wide leading-tight">
            Warranty Renewal <span className="hidden sm:inline">Checkout</span>
            <div className="sm:hidden text-xs font-normal normal-case opacity-90 mt-0.5">
              Checkout
            </div>
          </h1>
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border border-white flex items-center justify-center shrink-0">
            <ShieldCheck
              className="w-4 h-4 sm:w-5 sm:h-5 text-white"
              strokeWidth={2}
            />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-8 space-y-5 sm:space-y-4">
        {/* Order Summary Card */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden transition-colors">
          <div className="border-l-4 border-blue-600 px-4 py-3 border-b border-gray-100 dark:border-b-slate-800 transition-colors">
            <p className="text-xs font-bold text-gray-900 dark:text-slate-200 uppercase tracking-wide">
              Renewal Summary
            </p>
          </div>
          <div className="p-4 sm:p-5 space-y-5 sm:space-y-4">
            <div className="space-y-4 text-sm">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 sm:gap-4">
                <span className="text-gray-500 dark:text-gray-400 font-medium shrink-0">
                  Product
                </span>
                <span className="font-bold text-gray-900 dark:text-white sm:text-right leading-snug">
                  {productName}
                </span>
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-4">
                <span className="text-gray-500 dark:text-gray-400 font-medium">Renewal Plan</span>
                <span className="font-bold text-gray-900 dark:text-white sm:text-right">
                  {renewalDuration} Month Extension
                </span>
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-4">
                <span className="text-gray-500 dark:text-gray-400 font-medium">
                  Previous Expiry
                </span>
                <span className="font-bold text-gray-900 dark:text-white sm:text-right">
                  {fmt(previousWarrantyEndDate)}
                </span>
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-4">
                <span className="text-gray-500 dark:text-gray-400 font-medium">
                  New Expiry After Renewal
                </span>
                <span className="font-bold text-green-700 dark:text-green-400 sm:text-right">
                  {fmt(newExpiry)}
                </span>
              </div>
            </div>

            <hr className="border-gray-100 dark:border-slate-800 transition-colors" />

            <div className="flex items-center justify-between pt-1">
              <span className="text-gray-700 dark:text-slate-300 font-semibold text-sm sm:text-base">
                Total Amount
              </span>
              <span className="text-xl sm:text-2xl font-black text-gray-900 dark:text-white">
                ₹{Number(renewalPrice).toLocaleString("en-IN")}
              </span>
            </div>
          </div>
        </div>

        {/* Secure payment note */}
        <p className="text-center text-xs text-gray-400 dark:text-gray-500 flex items-center justify-center gap-1">
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
              clipRule="evenodd"
            />
          </svg>
          100% Secured Payment via Razorpay
        </p>

        <button
          onClick={handlePay}
          disabled={processing}
          className={`w-full flex items-center justify-center gap-2 py-3 sm:py-4 rounded-xl font-bold text-white text-sm sm:text-base uppercase tracking-wide transition-all ${
            processing
              ? "bg-blue-400 dark:bg-blue-800 cursor-not-allowed opacity-70"
              : "bg-[#0052cc] hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 shadow-md shadow-blue-200 dark:shadow-none"
          }`}
        >
          {processing
            ? "Processing..."
            : `Pay ₹${Number(renewalPrice).toLocaleString("en-IN")} with Razorpay`}
          {!processing && <ChevronRight className="w-5 h-5" />}
        </button>

        {/* Cancel */}
        <button
          onClick={() => navigate(-1)}
          className="w-full py-3 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 font-medium transition"
        >
          ← Go Back
        </button>
      </div>
    </div>
  );
}
